"use client";
import React, { useState, useEffect } from 'react';

function ContactForm() {
    const [formData, setFormData] = useState({
        name: '',
        whatsapp: '',
        company: '',
        message: '',
        hiddenField: '',
    });
    const [errors, setErrors] = useState({});
    const [toast, setToast] = useState({ show: false, message: "", type: "" });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [recaptchaToken, setRecaptchaToken] = useState("");

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    useEffect(() => {
        const stored = localStorage.getItem("formSubmittedAt");
        if (stored) {
            const submittedAt = new Date(parseInt(stored, 10));
            const now = new Date();
            const diffHours = (now - submittedAt) / (1000 * 60 * 60);

            if (diffHours < 4) {
                setSubmitted(true); // still within 4 hours → hide form
            } else {
                localStorage.removeItem("formSubmittedAt"); // expired → reset
            }
        }
    }, []);

    const handleSuccess = () => {
        setSubmitted(true);
        localStorage.setItem("formSubmittedAt", Date.now().toString());
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) newErrors.name = "Name is required";
        if (!formData.whatsapp.trim()) newErrors.whatsapp = "Whatsapp number is required";
        if (!formData.company.trim()) newErrors.company = "Company is required.";
        if (!formData.message.trim()) newErrors.message = "Message is required";

        const phoneRegex = /^\+\d{2,3}\d{6,14}$/; 
        if (formData.whatsapp && !phoneRegex.test(formData.whatsapp)) {
            newErrors.whatsapp = "Whatsapp number must be in format +6280012345678";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const showToast = (message, type) => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            showToast("Please fix the errors before submitting.", "error");
            return;
        }

        if (typeof window === "undefined" || !window.grecaptcha) {
            alert("reCAPTCHA is still loading. Please try again in a moment.");
            return;
        }

        setLoading(true);

        if (window.grecaptcha) {
            window.grecaptcha.ready(async () => {
                try {
                    const token = await window.grecaptcha.execute(
                        process.env.REACT_APP_RECAPTCHA_SITE_KEY,
                        { action: "submit" }
                    );

                    setRecaptchaToken(token);

                    const response = await fetch(process.env.REACT_APP_BASE_URL + "/api/send-whatsapp", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ 
                            ...formData, 
                            recaptchaToken: token
                        })
                    });

                    const result = await response.json();
                    if (result.error) {
                        showToast("Error: " + result.error, "error");
                    } else {
                        handleSuccess();
                        showToast("Message sent successfully!", "success");
                        setFormData({ name: "", whatsapp: "", company: "", message: "", hiddenField: "" });
                    }
                } catch (error) {
                    console.error("reCAPTCHA execution failed", error);
                } finally {
                    setLoading(false); 
                }
            });
        } else {
            alert("reCAPTCHA not loaded");
        }
    };

    return (
        <section className="flex-1">
            {!submitted ? (
                <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <input
                    type="text"
                    name="hiddenField"
                    value={formData.hiddenField}
                    onChange={handleInputChange}
                    style={{ display: "none" }}
                />
                <div className="flex flex-col gap-2">
                    <label htmlFor="name" className="mb-1 text-sm font-semibold leading-snug text-teal-600">
                        Name
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="John Doe"
                        className={`px-4 py-3 w-full text-base leading-normal rounded-lg border border-zinc-200 text-zinc-600 ${
                            errors.name ? "border-red-500" : "border-gray-300"
                        }`}
                    />
                    {errors.name && <p className="text-red-600 text-sm mt-0">{errors.name}</p>}
                </div>
                <div className="flex flex-col gap-2">
                    <label htmlFor="whatsapp" className="mb-1 text-sm font-semibold leading-snug text-teal-600">
                        Whatsapp Number
                    </label>
                    <input
                        type="tel"
                        id="whatsapp"
                        name="whatsapp"
                        value={formData.whatsapp}
                        onChange={handleInputChange}
                        placeholder="+6280012345678"
                        className={`px-4 py-3 w-full text-base leading-normal rounded-lg border border-zinc-200 text-zinc-600 ${
                            errors.whatsapp ? "border-red-500" : "border-gray-300"
                        }`}
                    />
                    {errors.whatsapp && <p className="text-red-600 text-sm mt-0">{errors.whatsapp}</p>}
                </div>
                <div className="flex flex-col gap-2">
                    <label htmlFor="company" className="mb-1 text-sm font-semibold leading-snug text-teal-600">
                        Company
                    </label>
                    <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        placeholder="Your Company"
                        className={`px-4 py-3 w-full text-base leading-normal rounded-lg border border-zinc-200 text-zinc-600 ${
                            errors.company ? "border-red-500" : "border-gray-300"
                        }`}
                    />
                    {errors.company && <p className="text-red-600 text-sm mt-0">{errors.company}</p>}
                </div>
                <div className="flex flex-col gap-2">
                    <label htmlFor="message" className="mb-1 text-sm font-semibold leading-snug text-teal-600">
                        Message
                    </label>
                    <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about your project..."
                        className={`px-4 py-3 w-full h-40 text-base leading-normal rounded-lg border resize-none border-zinc-200 text-zinc-600 ${
                            errors.message ? "border-red-500" : "border-gray-300"
                        }`}
                    />
                    {errors.message && <p className="text-red-600 text-sm mt-0">{errors.message}</p>}
                </div>
                <input type="hidden" name="recaptchaToken" value={recaptchaToken} />
                <button
                    type="submit"
                    className="px-6 py-3.5 w-full text-base font-semibold leading-normal text-white bg-teal-600 rounded-lg cursor-pointer border-[nonepx] hover:bg-teal-700 transition-colors"
                >
                    Send Message
                </button>
                </form>
            ) : (
                <>
                    <h2 className="text-2xl font-bold leading-none text-teal-600 z-10 mr-0 max-md:max-w-full max-md:text-2xl">Message received! We'll get back to you shortly.</h2> 
                    <p className="mb-8 text-2xl text-amber-500 max-sm:text-2xl">Thank you for choosing Kiosqu.</p>
                </>
            )}

            {/* Toast Notification */}
            {toast.show && (
            <div
                className={`fixed top-20 right-5 px-4 py-2 rounded shadow-lg text-white transform transition-transform duration-500 ease-out
                ${toast.type === "success" ? "bg-green-600" : "bg-red-600"}
                ${toast.show ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"}
                `}
            >
                {toast.message}
            </div>
            )}

            {/* Loader Overlay */}
            {loading && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-teal-500 border-solid"></div>
                </div>
            )}
        </section>
    );
}

export default ContactForm;
